import React, { useRef, useState } from 'react';
import CallMadeIcon from '@mui/icons-material/CallMade';
import styles from './style.module.scss';
import { buildClassName } from 'utils/build-class-name';
import { useKeenSlider } from 'keen-slider/react';
import PlayButtonIcon from 'components/icon/play-button';
import Button, { PRIMARY_THEME, SECONDARY_THEME } from 'components/control/button';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { ExpCommercialUsThemeColors } from '@tenants/definitions/exp/commercial/us/theme-config';

const NUM_SLIDES = 6;

type PlayButtonProps = {
  isPlaying: boolean;
  onClick: () => void;
};

const PlayButton = ({ isPlaying, onClick }: PlayButtonProps) => {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <button
      id="playButton"
      data-testid="play-button"
      className={buildClassName(styles['play-button'], isPlaying && styles.invisible)}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <PlayButtonIcon
        foregroundFill={isHovered || isPlaying ? ExpCommercialUsThemeColors.primary : ExpCommercialUsThemeColors.primaryBackground}
        backgroundFill={isHovered || isPlaying ? ExpCommercialUsThemeColors.primaryBackground : ExpCommercialUsThemeColors.primary}
      />
    </button>
  );
};

type VideoVariantProps = {
  mediaType: 'video';
  autoPlay?: boolean;
  webmSrc: string;
  mp4Src?: string;
  posterSrc?: string;
};

type ImageVariantProps = {
  mediaType: 'image';
  imageSrc: string;
};

export type GetInTouchProps = {
  title: string;
  description: string;
  contactButton: string;
  joinButton: string;
} & (VideoVariantProps | ImageVariantProps);

export default function GetInTouch(props: GetInTouchProps) {
  const { title, description, contactButton, joinButton } = props;
  
  const animation = { duration: 16000, easing: (t: number) => t };
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [sliderRef] = useKeenSlider({
    loop: true,
    renderMode: 'performance',
    drag: false,
    slides: {
      perView: 'auto',
      spacing: 5,
    },
    created(s) {
      s.moveToIdx(NUM_SLIDES - 1, true, animation);
    },
    updated(s) {
      s.moveToIdx(s.track.details.abs + NUM_SLIDES - 1, true, animation);
    },
    animationEnded(s) {
      s.moveToIdx(s.track.details.abs + NUM_SLIDES - 1, true, animation);
    },
  });

  const handleClickPlayButton = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const { t } = useTranslation();

  return (
    <div className={styles.component}>
      <h2>{t(title, { defaultValue: 'Get in Touch' })}</h2>
      <div className={styles['cta-and-video-container']}>
        <div className={styles['cta-container']}>
          <div className={styles['cta-text']}>
            <p>{t(description, { defaultValue: 'Need more information? Get in touch with an expert at eXp Commercial today.' })}</p>
          </div>
          <div className={styles['cta-buttons']}>
            <a href="/contact-us">
              <Button theme={PRIMARY_THEME} label={t(contactButton, { defaultValue: 'Get in Touch' })} />
            </a>
            <a href="/become-an-advisor">
              <Button theme={SECONDARY_THEME} label={t(joinButton, { defaultValue: 'Join Us' })} />
            </a>
          </div>
        </div>
        <div className={styles['media-container']}>
          {props.mediaType === 'video' ? (
            <>
              {
                // product decided they didn't want a play button after all, sorry Alex :(
                // but we can keep this functionality here because we already made it so why not
                !props.autoPlay && <PlayButton isPlaying={isPlaying} onClick={handleClickPlayButton} />
              }
              <video 
                autoPlay={props.autoPlay} 
                ref={videoRef} 
                playsInline 
                muted 
                controls={false} 
                loop={true} 
                preload="none" 
                poster={props.posterSrc}>
                <source src={props.webmSrc} type="video/webm" />
                {props.mp4Src && <source src={props.mp4Src} type="video/mp4" />}
                Your browser doesn&apos;t support HTML video.
              </video>
            </>
          ) : (
            <Image src={props.imageSrc} fill alt={t(title, { defaultValue: 'Get in Touch' })} />
          )}
        </div>
      </div>
      <div ref={sliderRef} className={buildClassName(styles['slider'], 'keen-slider')}>
        {Array.from({ length: NUM_SLIDES }).map((_, index) => (
          <div key={index} className={buildClassName(styles['slide'], 'keen-slider__slide')}>
            <div>Get in Touch</div>
            <CallMadeIcon />
          </div>
        ))}
      </div>
    </div>
  );
}
